/**
 * MetaAppReviewCallLog — iter-254 (Feb 2026)
 * ==========================================
 * Shows the raw Graph API call history for a single capability. Every
 * row exposes: timestamp, permission, HTTP status, endpoint, and — on
 * click — the full JSON response body from Meta.
 *
 * Route: /marketing/meta-app-review/:capId/calls
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, CheckCircle2, XCircle, ChevronDown, ChevronUp,
} from 'lucide-react';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MetaAppReviewCallLog() {
  const { capId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = useCallback((ar, en) => (isAr ? ar : en), [isAr]);

  const [cap, setCap] = useState(null);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/meta-review/capabilities/${capId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/meta-review/capabilities/${capId}/calls?limit=200`, { headers: getAuthHeaders() }),
      ]);
      setCap(r1.data?.data || null);
      setCalls(r2.data?.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [capId]);

  useEffect(() => { load(); }, [load]);

  const breadcrumb = [
    { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
    { label_ar: 'التسويق', label_en: 'Marketing', path: '/dashboard?view=marketing' },
    { label_ar: 'CLOUDGM FZ LLC (تسويق)', label_en: 'CLOUDGM FZ LLC (Marketing)', path: '/marketing' },
    { label_ar: 'Meta App Review Testing', label_en: 'Meta App Review Testing', path: '/marketing/meta-app-review' },
    { label_ar: cap ? (isAr ? cap.name_ar : cap.name_en) : t('السجل', 'Log'),
      label_en: cap ? cap.name_en : 'Log', path: null },
  ];

  return (
    <div className="min-h-screen bg-[#030303] text-white" dir={isAr ? 'rtl' : 'ltr'}>
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-[#d4af37]/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader onBack={() => navigate('/marketing/meta-app-review')} />
      <div className="border-b border-white/5 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={breadcrumb} />
        </div>
      </div>

      <main className="relative z-10 py-6 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white" data-testid="meta-log-title">
                {cap ? (isAr ? cap.name_ar : cap.name_en) : t('سجل الاستدعاءات', 'Call Log')}
              </h1>
              {cap && (
                <p className="text-[12px] text-white/50 mt-1">
                  {cap.progress?.total_current} / {cap.progress?.total_required} · {cap.progress?.percent}%
                </p>
              )}
            </div>
            <Button onClick={load} variant="outline" size="sm" className="border-white/10 text-white/70">
              <RefreshCw className="h-4 w-4 me-2" />
              {t('تحديث', 'Refresh')}
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-amber-400" />
            </div>
          ) : calls.length === 0 ? (
            <div className="text-center py-24 text-white/40 text-sm">
              {t('لا توجد استدعاءات مسجلة بعد. اضغط "تشغيل الكل" في الصفحة السابقة لبدء الاختبار.',
                 'No calls recorded yet. Click "Run All" on the previous page to start testing.')}
            </div>
          ) : (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.03] border-b border-white/10">
                  <tr className="text-[11px] uppercase text-white/50">
                    <th className="px-3 py-2 text-start">{t('الوقت', 'Timestamp')}</th>
                    <th className="px-3 py-2 text-start">{t('الصلاحية', 'Permission')}</th>
                    <th className="px-3 py-2 text-start">HTTP</th>
                    <th className="px-3 py-2 text-start">Endpoint</th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c) => {
                    const open = !!expanded[c.id];
                    return (
                      <React.Fragment key={c.id}>
                        <tr className={`border-b border-white/5 cursor-pointer hover:bg-white/[0.02] ${
                          c.ok ? '' : 'bg-red-500/[0.03]'
                        }`}
                            onClick={() => setExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))}
                            data-testid={`meta-call-row-${c.id}`}>
                          <td className="px-3 py-2 text-[11px] text-white/60" dir="ltr">
                            {new Date(c.timestamp).toLocaleString(isAr ? 'ar-EG' : 'en-GB')}
                          </td>
                          <td className="px-3 py-2 font-mono text-[11px] text-white/80" dir="ltr">
                            {c.permission}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${
                              c.ok
                                ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30'
                                : 'bg-red-500/10 text-red-300 border-red-500/30'
                            }`}>
                              {c.ok ? <CheckCircle2 className="h-2.5 w-2.5" /> : <XCircle className="h-2.5 w-2.5" />}
                              {c.http_status || '—'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-[11px] text-white/60 font-mono truncate max-w-[420px]" dir="ltr">
                            {c.endpoint}
                          </td>
                          <td className="px-3 py-2 text-white/40">
                            {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          </td>
                        </tr>
                        {open && (
                          <tr>
                            <td colSpan={5} className="p-0">
                              <pre className="text-[10px] text-white/70 bg-black/40 border-y border-white/5 p-3 overflow-x-auto max-h-64" dir="ltr">
{JSON.stringify(c.response || { error: c.error }, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
